import { useQuery } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { ReportFrame, reportMeta } from "./Frame";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Money,
  ModelledFootnote,
  ModelledTag,
  Footnote,
  ShareBar,
  SubHeading,
  plural,
} from "../kit";

/**
 * Subcategory names, as a human would write them.
 *
 * The client's own sheet mixes cases — `DISPOSABLES` and `ENDO` shout while
 * `Materials` and `Preventative/Hygiene` do not — and a breakdown that renders
 * both verbatim reads as two different systems bolted together. **Display
 * only.** The stored value is the client's, the pricing rules match on it, and
 * nothing here writes it back; this only decides how it is drawn.
 */
const properCase = (raw: string): string =>
  raw
    .toLowerCase()
    .replace(
      /(^|[\s/&(-])([a-z])/g,
      (_match, lead: string, letter: string) => lead + letter.toUpperCase(),
    );

/**
 * Report — Warehouse inventory value. "What is sitting on the shelves worth?"
 *
 * The total is `warehouse on-hand × Nexora cost`, split by category and
 * subcategory, with the eight biggest single holdings named. Units travel next
 * to every dollar figure: a valuation nobody can check against the shelf is a
 * valuation nobody should read out.
 *
 * **The caveat is the feature here.** Nearly every cost behind this number is
 * modelled — derived inside the client's stated discount band because no
 * supplier cost exists in any document we have. So:
 *
 *   1. `ModelledFootnote` renders first, above the total, whenever any
 *      contributing item is modelled. It has no dismiss control and this file
 *      gives it none — there is no state that hides it.
 *   2. The headline total is *labelled* "Estimate", not merely accompanied by a
 *      line above it, because a screenshot of the number is what ends up in a
 *      deck.
 *   3. The exact dollars resting on modelled cost are stated under it.
 *
 * Carson must not be able to point at this number on a projector and imply it
 * is real. Three separate places on this screen say otherwise.
 *
 * The three blocks are sections of the one report card, separated by a rule and
 * a heading. They used to be three nested cards inside the report's own frame,
 * which is a white box on a white box on a white box.
 */
export function InventoryValue() {
  const data = useQuery(api.reports.inventoryValue.data);

  const modelledCost = data?.cost;

  return (
    <ReportFrame
      {...reportMeta("inventory-value")}
      question="What is sitting on the shelves worth?"
      data={data}
      count={data?.count}
      empty={{
        title: "Nothing on the warehouse shelves",
        body: "No warehouse item has any units on hand, so there is no stock to value.",
      }}
      footnote={
        data ? (
          <>
            Valued at Nexora cost — warehouse on-hand × unit cost. Clinic-held
            stock is not counted here. {data.reservedUnits}{" "}
            {plural(data.reservedUnits, "unit")} (
            <Money cents={data.reservedValueCents} />) are already reserved
            against clinic requests, leaving{" "}
            <Money cents={data.availableValueCents} /> free to dispatch.
          </>
        ) : null
      }
    >
      {data && modelledCost ? (
        <div className="space-y-4">
          {/* Non-dismissible, and first on the screen — before the number it is
              about. Renders nothing only when nothing is modelled. */}
          <ModelledFootnote
            modelled={modelledCost.modelled}
            total={modelledCost.total}
            bandMin={modelledCost.bandMin}
            bandMax={modelledCost.bandMax}
          />

          {/* The headline. Still labelled on the number itself rather than only
              above it — the `ModelledTag` travels with the figure either way. */}
          <div>
            {modelledCost.modelled > 0 ? (
              <div className="flex items-center gap-2">
                <ModelledTag label="Estimate" />
                <span className="text-xs text-muted-foreground">
                  built on modelled cost
                </span>
              </div>
            ) : null}
            <div className="num mt-1 text-3xl font-semibold tracking-tight">
              <Money cents={data.totalValueCents} />
            </div>
            {/* The unit count next to the money is the *valued* one. Pairing
                the total with every unit on the shelf would credit it with
                units it never valued. */}
            <div className="num mt-0.5 text-sm text-muted-foreground">
              {data.valuedUnits} {plural(data.valuedUnits, "unit")} across{" "}
              {modelledCost.total} {plural(modelledCost.total, "item")}
            </div>
            {modelledCost.pricePendingUnits > 0 ? (
              <div className="num mt-0.5 text-xs text-muted-foreground">
                {data.totalUnits} units on the shelf in all —{" "}
                {modelledCost.pricePendingUnits} of them, across{" "}
                {modelledCost.pricePendingItems}{" "}
                {plural(modelledCost.pricePendingItems, "item")}, carry no price
                on the sheet and are not valued at all.
              </div>
            ) : null}
            {modelledCost.modelled > 0 ? (
              <Footnote tone="caution" className="mt-2">
                <Money cents={modelledCost.modelledValueCents} /> of this total (
                {modelledCost.modelledValueSharePct}%) rests on modelled costs. It
                is not a supplier-verified valuation and must not be presented as
                one.
              </Footnote>
            ) : null}
          </div>

          {/* By category, then subcategory. Bars rather than a chart: they read
              at 390px and cannot mislead about a total. */}
          <div className="border-t pt-4">
            <SubHeading as="h4">Where the value sits</SubHeading>
            <ul className="mt-2 space-y-3">
              {data.categories.map((category) => (
                <li key={category.key}>
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="truncate text-sm font-medium">
                      {category.label}
                    </span>
                    <span className="num shrink-0 text-sm font-medium">
                      <Money cents={category.valueCents} />
                    </span>
                  </div>
                  <ShareBar
                    size="sm"
                    className="mt-1"
                    pct={category.sharePct}
                    minPct={1}
                  />
                  <div className="num mt-0.5 text-xs text-muted-foreground">
                    {category.sharePct}% · {category.units}{" "}
                    {plural(category.units, "unit")} · {category.items}{" "}
                    {plural(category.items, "item")}
                  </div>

                  <ul className="mt-1.5 space-y-1 border-l pl-3">
                    {category.subcategories.map((sub) => (
                      <li
                        key={`${category.key}:${sub.name}`}
                        className="flex items-baseline justify-between gap-3 text-xs text-muted-foreground"
                      >
                        <span className="truncate">
                          {properCase(sub.name)} · {sub.units}u
                        </span>
                        <span className="num shrink-0">
                          <Money cents={sub.valueCents} />
                        </span>
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          </div>

          {/* The biggest single holdings — where a recount is worth doing. */}
          <div className="border-t pt-4">
            <SubHeading as="h4">Biggest holdings</SubHeading>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Item</TableHead>
                  <TableHead className="text-right">Value</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.topHoldings.map((holding) => (
                  <TableRow key={holding.itemId}>
                    <TableCell className="whitespace-normal">
                      <div className="font-medium">{holding.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {properCase(holding.subcategory)} · {holding.vendor}
                        {holding.packSize ? ` · ${holding.packSize}` : ""}
                      </div>
                      <div className="num mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                        <span>
                          on hand{" "}
                          <span className="font-medium text-foreground">
                            {holding.onHand}
                          </span>
                        </span>
                        <span>
                          reserved{" "}
                          <span className="font-medium text-foreground">
                            {holding.reserved}
                          </span>
                        </span>
                        <span>
                          unit cost{" "}
                          <span className="font-medium text-foreground">
                            <Money cents={holding.unitCostCents} />
                          </span>
                        </span>
                        {holding.costIsModelled ? <ModelledTag /> : null}
                      </div>
                    </TableCell>
                    <TableCell className="num text-right align-top">
                      <div className="font-medium">
                        <Money cents={holding.valueCents} />
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {holding.sharePct}%
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      ) : null}
    </ReportFrame>
  );
}
